/**
 * Transaction Display Utility
 *
 * Single source of truth for how a `wallet_transactions` row is rendered.
 *
 * This existed as four near-identical copies (Welcome, WalletDashboard, Notifications,
 * AcountStatement), which had drifted: the same transfer showed as "Transfer Received"
 * on /welcome, "From QA" on /wallet, "Transfer In" on /account-statement and
 * "Transfer Received" + "From QA Referred Seven" on /notifications. Keep new
 * transaction types in here rather than in a screen.
 */

const CREDIT_TYPES = ['deposit', 'buy', 'transfer_in', 'referral_bonus', 'admin_lending'];

/** Money coming in (rendered green with a leading +). */
export const isCredit = (type) => CREDIT_TYPES.includes(type);

/**
 * A row that was credited and later taken back — currently only referral bonuses
 * reversed because the referred user never verified their email. The row and its
 * amount are kept for audit, but no money is attached to it any more, so it must
 * never render as a credit or count toward a total.
 */
export const isReversed = (tx) => tx.status === 'reversed';

/** Settled on the blockchain rather than in Sigma Bank's own ledger. */
export const isOnChain = (tx) => tx.is_onchain === true || tx.transaction_type === 'deposit';

/**
 * Counterparty name for a transfer.
 * `sender_name`/`recipient_name` are enriched by walletServiceEnhanced.getUserTransactions;
 * when a screen uses a service that doesn't enrich, fall back to parsing the description
 * the transfer RPC wrote ("Transfer from <name>" / "Transfer to <name>").
 */
export const counterpartyName = (tx) => {
    if (tx.transaction_type === 'transfer_in') {
        if (tx.sender_name) return tx.sender_name;
        const m = (tx.description || '').match(/Transfer from (.+)/);
        return m ? m[1].trim() : null;
    }
    if (tx.transaction_type === 'transfer_out') {
        if (tx.recipient_name) return tx.recipient_name;
        const m = (tx.description || '').match(/Transfer to (.+)/);
        return m ? m[1].trim() : null;
    }
    return null;
};

const BASE_LABELS = {
    deposit:        'Blockchain Deposit',
    buy:            'Buy Crypto',
    withdrawal:     'Withdrawal',
    transfer_in:    'Transfer Received',
    transfer_out:   'Transfer Sent',
    referral_bonus: 'Referral Bonus',
    admin_lending:  'Admin Credit',
};

/**
 * Primary row label. Names the counterparty when one is known, so the same row reads
 * the same way on every screen.
 */
export const txLabel = (tx) => {
    const name = counterpartyName(tx);
    if (name && tx.transaction_type === 'transfer_in')  return `From ${name}`;
    if (name && tx.transaction_type === 'transfer_out') return `To ${name}`;
    return BASE_LABELS[tx.transaction_type] || tx.transaction_type?.replace(/_/g, ' ') || 'Transaction';
};

/** Type-only label, for surfaces that show the counterparty on a separate line. */
export const txTypeLabel = (type) => BASE_LABELS[type] || 'Transaction';

const ICONS = {
    deposit:        'bx-arrow-to-bottom',
    buy:            'bx-credit-card',
    withdrawal:     'bx-arrow-from-bottom',
    transfer_in:    'bx-transfer-alt',
    transfer_out:   'bx-transfer-alt',
    referral_bonus: 'bx-gift',
    admin_lending:  'bx-dollar',
};

export const txIcon = (type) => ICONS[type] || 'bx-bell';

/** Secondary line describing what happened. */
export const txSubtitle = (tx) => {
    if (isReversed(tx)) return 'Reversed — referred user never verified their email';
    const name = counterpartyName(tx);
    if (tx.transaction_type === 'transfer_in'  && name) return `From ${name}`;
    if (tx.transaction_type === 'transfer_out' && name) return `To ${name}`;
    switch (tx.transaction_type) {
        case 'deposit':        return 'On-chain deposit confirmed';
        case 'buy':            return 'Card purchase credited';
        case 'referral_bonus': return 'Referral reward credited';
        case 'withdrawal':     return 'Withdrawal processed';
        default:               return 'Wallet activity';
    }
};

// Credits green, debits red. Blue previously did double duty as both the "off-chain"
// badge colour and the debit amount colour, which read as ambiguous.
export const CREDIT_COLOR = '#10b981';
export const DEBIT_COLOR = '#ef4444';
export const MUTED_COLOR = 'rgba(255,255,255,0.4)';

export const txAmountColor = (credit) => (credit ? CREDIT_COLOR : DEBIT_COLOR);

/** Colour for a row's amount, accounting for reversal. */
export const txRowAmountColor = (tx, credit) =>
    (isReversed(tx) ? MUTED_COLOR : txAmountColor(credit));

/** '+' / '-' — but a reversed row carries no sign, because it moved no money. */
export const txAmountPrefix = (tx, credit) =>
    (isReversed(tx) ? '' : (credit ? '+' : '-'));

export const formatUsd = (v) =>
    Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const timeAgo = (dateStr) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60)     return 'Just now';
    if (diff < 3600)   return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400)  return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 172800) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
